/* eslint-disable no-empty */
/**
 * Phase A R5 F3 — Library pre-analysis hook.
 *
 * After the user picks a folder, we automatically pre-decode each audio
 * track in the background (one at a time) and run BPM + key detection.
 * Results are cached in IndexedDB keyed by `${folderName}/${trackPath}`.
 *
 * On subsequent loads, library rows render with `BPM · Key · duration`
 * badges instantly. Dragging an already-analyzed track to a deck means
 * BPM is known immediately without re-running the worker.
 *
 * The library intentionally does not parse untrusted ID3 or container metadata
 * in the browser. File names, browser media decoding, and BPM analysis provide
 * the safe baseline; optional tags can be added later through a maintained
 * server-side parser with strict resource limits.
 */
import { useEffect, useRef, useState } from "react";

const IDB_NAME = "stackroom-dj-library";
const IDB_STORE = "analysis";

function idbOpen() {
  return new Promise((res, rej) => {
    const req = indexedDB.open(IDB_NAME, 2);
    req.onupgradeneeded = () => {
      try { req.result.createObjectStore("handles"); } catch {}
      try { req.result.createObjectStore(IDB_STORE); } catch {}
    };
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}
async function idbGet(key) {
  try {
    const db = await idbOpen();
    return new Promise((res) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => res(req.result || null);
      req.onerror = () => res(null);
    });
  } catch { return null; }
}
async function idbSet(key, value) {
  try {
    const db = await idbOpen();
    return new Promise((res) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => res(true);
      tx.onerror = () => res(false);
    });
  } catch { return false; }
}

async function analyzeFile(file, fileName = "") {
  // Do not parse untrusted ID3 or container headers in the browser. The
  // interface falls back to the file name and safe browser-decoded audio data.
  const title = null, artist = null, album = null, genre = null, year = null;

  // Decode on main thread (workers cannot).
  const buf = await file.arrayBuffer();
  const tmp = new OfflineAudioContext(1, 44100 * 60, 44100);
  const decoded = await tmp.decodeAudioData(buf);
  const sr = decoded.sampleRate;
  // Phase A R9 E — analyze MIDDLE 30s instead of first 30s. Intros are
  // often filtered down, fading in, or beatless which produces wildly
  // off-base BPM estimates. The middle section is where the kick is
  // consistently audible.
  const totalLen = decoded.length;
  const wanted = Math.min(totalLen, sr * 30);
  const mid = Math.floor(totalLen / 2);
  const start = Math.max(0, mid - Math.floor(wanted / 2));
  const limit = Math.min(totalLen - start, wanted);
  const mono = new Float32Array(limit);
  const ch0 = decoded.getChannelData(0);
  const ch1 = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : null;
  for (let i = 0; i < limit; i++) mono[i] = ch1 ? (ch0[start + i] + ch1[start + i]) * 0.5 : ch0[start + i];

  // Phase A R9 E — emphasize beat-range frequencies (kick drum 60–200 Hz)
  // using a simple one-pole low-pass before peak detection. Treble +
  // mid energy contaminates onset detection with hi-hats / vocals.
  const cutoff = 200; // Hz
  const RC = 1.0 / (2 * Math.PI * cutoff);
  const dt = 1.0 / sr;
  const alpha = dt / (RC + dt);
  let lpf = 0;
  for (let i = 0; i < limit; i++) {
    lpf = lpf + alpha * (mono[i] - lpf);
    mono[i] = lpf;
  }

  // Run BPM detection inline (we can't easily share the existing engine workers from here).
  // Use the same algorithm as bpmWorker.js so cached values are consistent.
  const bpm = estimateBPM(mono, sr);
  // Phase A R7 B4 — same half-BPM normalization as DjEngine.detectBPM,
  // applied to the library badge so the user sees consistent BPM values
  // before and after dragging a track onto a deck.
  let normalizedBpm = bpm.bpm || 0;
  if (normalizedBpm > 0) {
    if (normalizedBpm < 80) normalizedBpm *= 2;
    while (normalizedBpm > 180) normalizedBpm /= 2;
    normalizedBpm = Math.max(60, Math.min(200, normalizedBpm));
  }
  // Phase A R9 E — surface the analysis to the console so users
  // debugging "BPM looks wrong" can see raw → normalized → confidence
  // at a glance.
  // eslint-disable-next-line no-console
  console.log(`[BPM] track=${fileName || "?"} raw=${bpm.bpm?.toFixed(1)} normalized=${normalizedBpm.toFixed(1)} confidence=${bpm.confidence?.toFixed(2)} source=detector`);
  // Skip key detection here — relatively expensive; surface BPM + duration.
  return {
    bpm: normalizedBpm,
    confidence: bpm.confidence,
    peakTime: bpm.peakTime,
    durationSec: decoded.duration,
    sampleRate: sr,
    title,
    artist,
    album,
    genre,
    year,
  };
}

function estimateBPM(channelData, sampleRate) {
  const windowSize = Math.floor(sampleRate * 0.05);
  const energies = [];
  for (let i = 0; i + windowSize < channelData.length; i += windowSize) {
    let sum = 0;
    for (let j = 0; j < windowSize; j++) {
      const v = channelData[i + j]; sum += v * v;
    }
    energies.push(sum / windowSize);
  }
  const lookback = 16;
  const peakTimes = [];
  for (let i = lookback; i < energies.length - lookback; i++) {
    let avg = 0;
    for (let j = i - lookback; j < i + lookback; j++) avg += energies[j];
    avg /= (2 * lookback);
    if (energies[i] > avg * 1.5 && energies[i] > 0.005) {
      peakTimes.push((i * windowSize) / sampleRate);
    }
  }
  if (peakTimes.length < 4) return { bpm: 0, confidence: 0, peakTime: 0 };
  const intervals = [];
  for (let i = 1; i < peakTimes.length; i++) intervals.push(peakTimes[i] - peakTimes[i - 1]);
  const bin = {};
  for (const iv of intervals) {
    if (iv < 0.2 || iv > 1.5) continue;
    const k = Math.round(iv * 100) / 100;
    bin[k] = (bin[k] || 0) + 1;
  }
  let best = 0, bestCount = 0, total = 0;
  for (const k in bin) {
    total += bin[k];
    if (bin[k] > bestCount) { best = parseFloat(k); bestCount = bin[k]; }
  }
  if (!best) return { bpm: 0, confidence: 0, peakTime: peakTimes[0] || 0 };
  let bpm = 60 / best;
  while (bpm < 70) bpm *= 2;
  while (bpm > 180) bpm /= 2;
  return { bpm: Math.round(bpm * 10) / 10, confidence: total > 0 ? bestCount / total : 0, peakTime: peakTimes[0] };
}

export function useLibraryAnalysis(library) {
  const [meta, setMeta] = useState({}); // { 'folder/path': { bpm, durationSec, ... } }
  const [analyzing, setAnalyzing] = useState({}); // { 'folder/path': true } per-row spinner
  const queueRef = useRef([]);
  const runningRef = useRef(false);

  // Load cached metadata for the current track list on every folder change.
  useEffect(() => {
    if (!library?.tracks?.length) return;
    (async () => {
      const out = { ...meta };
      for (const t of library.tracks) {
        const key = `${library.folderName}/${t.path}`;
        if (out[key]) continue;
        const cached = await idbGet(key);
        if (cached) out[key] = cached;
      }
      setMeta(out);
      // Queue the un-analyzed tracks.
      const todo = library.tracks
        .filter((t) => !out[`${library.folderName}/${t.path}`])
        .slice(0, 50); // safety cap
      queueRef.current = todo;
      drain(library.folderName);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [library?.folderName, library?.tracks?.length]);

  const drain = async (folderName) => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      while (queueRef.current.length) {
        const t = queueRef.current.shift();
        const key = `${folderName}/${t.path}`;
        setAnalyzing((s) => ({ ...s, [key]: true }));
        try {
          const m = await analyzeFile(t.file);
          await idbSet(key, m);
          setMeta((s) => ({ ...s, [key]: m }));
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[useLibraryAnalysis] failed for ${t.path}:`, e);
        } finally {
          setAnalyzing((s) => { const n = { ...s }; delete n[key]; return n; });
        }
      }
    } finally {
      runningRef.current = false;
    }
  };

  // Convenience: enrich tracks with their cached metadata.
  // Phase A R6 B11 — also expose title/artist/album/genre/year for sorting.
  // Fallbacks: title → filename without extension, artist → "Unknown Artist",
  // genre → "Unknown". These keep sort comparators stable.
  const enriched = (library?.tracks || []).map((t) => {
    const m = meta[`${library?.folderName}/${t.path}`];
    const baseName = t.name.replace(/\.[^.]+$/, "");
    return {
      ...t,
      bpm:          m?.bpm,
      durationSec:  m?.durationSec,
      analyzed:     !!m,
      title:        m?.title  || baseName,
      artist:       m?.artist || "Unknown Artist",
      album:        m?.album  || "",
      genre:        m?.genre  || "Unknown",
      year:         m?.year   || null,
    };
  });

  // Phase A R10 E — force re-analyze a single track. Bumps the cached
  // IDB entry, re-runs the analysis pipeline (MIDDLE-30s window +
  // 200Hz LPF per R9-E), and updates the per-row spinner state. The
  // library UI calls this from the per-row Re-analyze button.
  const reanalyzeOne = async (track) => {
    if (!library?.folderName || !track?.path || !track?.file) {
      // eslint-disable-next-line no-console
      console.warn("[useLibraryAnalysis] reanalyzeOne: missing folderName/path/file", { folderName: library?.folderName, path: track?.path, hasFile: !!track?.file });
      return null;
    }
    const key = `${library.folderName}/${track.path}`;
    setAnalyzing((s) => ({ ...s, [key]: true }));
    try {
      // Invalidate the existing cache row so analyzeFile doesn't return early.
      try {
        const db = await idbOpen();
        await new Promise((res) => {
          const tx = db.transaction(IDB_STORE, "readwrite");
          tx.objectStore(IDB_STORE).delete(key);
          tx.oncomplete = () => res(true);
          tx.onerror = () => res(false);
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[useLibraryAnalysis] idb delete failed (continuing):", e);
      }
      const m = await analyzeFile(track.file, track.name || track.path);
      await idbSet(key, m);
      setMeta((s) => ({ ...s, [key]: m }));
      // eslint-disable-next-line no-console
      console.log(`[useLibraryAnalysis] reanalyzed key=${key} bpm=${m.bpm} dur=${m.durationSec}`);
      return m;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[useLibraryAnalysis] reanalyze failed for ${track.path}:`, e);
      return null;
    } finally {
      setAnalyzing((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  return {
    tracks: enriched,
    analyzing,
    reanalyzeOne,
  };
}
